/*
 * Backbone Craft Co — Wishlist
 * Multiple named lists per customer (a primary "Wishlist" that can't be
 * deleted, plus any number of custom lists), stored in Postgres for
 * logged-in customers (loaded through the app proxy's /lists route, saved
 * through the worker -- see backbone-wishlist-worker and backbone-customer-
 * account-proxy), localStorage for guests. The worker owns the real schema
 * validation; this file mirrors those same rules client-side for a
 * responsive UI, not as the source of truth. The old customer metafield
 * custom.wishlist is no longer read or written (backbone-wishlist-worker#34).
 */

const BackboneWishlist = (() => {

  // ─── CONFIG ────────────────────────────────────────────────────────────────
  const STORAGE_KEY = 'backbone_wishlist';
  const API_VERSION = '2026-07';
  const PRIMARY_LIST_ID = 'wishlist';
  const PRIMARY_LIST_NAME = 'Wishlist';
  // Still needed for the Storefront API product lookups in
  // renderWishlistPage() -- reading product data is a normal Storefront
  // API operation, unlike saving a list (see saveToServer).
  const STOREFRONT_TOKEN = document.querySelector('meta[name="shopify-storefront-token"]')?.content;
  const SHOP_DOMAIN = document.querySelector('meta[name="shopify-domain"]')?.content || window.Shopify?.shop;
  const CUSTOMER_ID = document.querySelector('meta[name="customer-id"]')?.content;
  const LOGIN_URL = document.querySelector('meta[name="wishlist-login-url"]')?.content || '/account/login';
  const ALL_PRODUCTS_URL = document.querySelector('meta[name="wishlist-all-products-url"]')?.content || '/collections/all';
  // customer.access_token renders as an empty string under this store's
  // Customer Accounts system (confirmed live: the meta tag is present but
  // content="" while genuinely logged in), so a token-based login check was
  // always false -- every "logged in" session was silently falling through
  // to the guest/localStorage path instead of ever attempting a server
  // save. customer.id doesn't have this problem, so use that instead.
  const IS_LOGGED_IN = !!CUSTOMER_ID;

  // ─── STATE ─────────────────────────────────────────────────────────────────
  // lists: [{ id, name, primary, kind: 'wishlist'|'cart', items: [{ productId, variantId, quantity? }] }]
  // kind defaults to 'wishlist' (see normalizeLists) -- existing lists are
  // implicitly that. Saved Carts (#30) are 'cart'-kind: same shape, plus a
  // quantity on each item and no sharing support (see saveCurrentCartAsList).
  let lists = [];
  // True when a logged-in customer's lists could not be loaded from the
  // server. `lists` is then NOT the customer's real data, and every save posts
  // the whole state, so saving would overwrite what is actually stored --
  // saveWishlist refuses while this is set.
  let listsLoadFailed = false;
  // Which list the wishlist page is currently showing. Defaults to the
  // primary list once init() runs.
  let activeListId = PRIMARY_LIST_ID;
  // Set while the wishlist page is showing the "this list has items --
  // move them or delete anyway?" panel for a given list id.
  let pendingDeleteListId = null;
  // Purchasable variants for whatever list is currently rendered, set by
  // renderListItems -- lets "Add all to cart" skip sold-out items without
  // a second Storefront round trip to re-check availability.
  let activeListAddableItems = [];
  // The variant currently selected on a product page, kept in sync via
  // the theme's own variant-change pub/sub event (see bindVariantChangeListener).
  // null everywhere except a product page that actually has a variant
  // picker -- a single-variant product has no picker and no real choice
  // to record, so it's left null and behaves exactly like pre-#9 data.
  let currentProductVariantId = null;
  // Global "notify me about restocks/price-drops" preference (issue #11)
  // -- null until fetched (logged-in only; guests have no Klaviyo profile
  // to key this off), then true/false once known.
  let notifyPreferenceEnabled = null;

  // ─── INIT ──────────────────────────────────────────────────────────────────
  async function init() {
    // A shared-list link (see buildShareUrl) always lands on this same
    // page -- reusing it rather than a separate template means no extra
    // Admin setup, and it already has everything needed: the container,
    // the Storefront product-lookup code, the styling. This is a
    // completely separate, read-only rendering path -- it never touches
    // the visiting browser's own wishlist state (guest or logged in).
    const shareParams = getShareParamsFromUrl();
    if (shareParams && getWishlistPageContainer()) {
      await renderSharedListView(shareParams);
      return;
    }

    if (IS_LOGGED_IN) {
      const loaded = await loadFromServer();
      if (loaded) {
        lists = loaded;
        await mergeGuestWishlist();
      } else {
        lists = normalizeLists([]);
        listsLoadFailed = true;
        showToast("We couldn't load your saved lists. Please refresh the page.");
      }
    } else {
      lists = loadFromLocalStorage();
    }

    activeListId = PRIMARY_LIST_ID;
    const requestedListId = new URLSearchParams(window.location.search).get('list');
    if (requestedListId && findList(requestedListId)) {
      activeListId = requestedListId;
    }
    currentProductVariantId = getSelectedVariantId();

    renderHearts();
    renderListPickers();
    updateBadge();
    bindEvents();
    bindVariantChangeListener();

    if (getWishlistPageContainer()) {
      if (IS_LOGGED_IN) {
        notifyPreferenceEnabled = await fetchNotifyPreference();
      }
      renderWishlistPage();
    }
  }

  // Only ever called from the wishlist page (not fetched sitewide) --
  // there's nowhere else this preference is shown or needed.
  async function fetchNotifyPreference() {
    try {
      const response = await fetch('/apps/wishlist/notify-preference');
      const result = await response.json();
      return result.success ? result.enabled : false;
    } catch (err) {
      console.error('Backbone Wishlist: Failed to load notify preference', err);
      return false;
    }
  }

  // ─── NORMALIZE / MIGRATE ───────────────────────────────────────────────────
  // Wraps a pre-Wishlist-2.0 flat array of product id strings into a single
  // primary list. Both the metafield (until a customer's next save) and
  // localStorage may still hold this old shape.
  function isLegacyFlatArray(data) {
    return Array.isArray(data) && data.every(entry => typeof entry === 'string');
  }

  // quantity only ever appears on a cart-kind list's items (see the worker's
  // isValidItem) -- carried through as a number when present, dropped
  // otherwise so a plain wishlist item stays exactly the old shape.
  function normalizeItem(item) {
    if (typeof item === 'string') return { productId: item, variantId: null };
    const base = { productId: String(item.productId), variantId: item.variantId ?? null };
    return typeof item.quantity === 'number' ? { ...base, quantity: item.quantity } : base;
  }

  function normalizeLists(data) {
    if (isLegacyFlatArray(data)) {
      return [{
        id: PRIMARY_LIST_ID,
        name: PRIMARY_LIST_NAME,
        primary: true,
        kind: 'wishlist',
        items: data.map(normalizeItem),
        sharing: false,
        shareToken: null,
      }];
    }

    if (!Array.isArray(data)) {
      return [{ id: PRIMARY_LIST_ID, name: PRIMARY_LIST_NAME, primary: true, kind: 'wishlist', items: [], sharing: false, shareToken: null }];
    }

    const normalized = data.map(list => ({
      id: String(list.id),
      name: String(list.name || 'Untitled list'),
      primary: !!list.primary,
      // Mirrors the worker's own default-to-"wishlist" rule (parseWishlist)
      // -- anything other than the literal "cart" is treated as a wishlist,
      // same as data with no kind field at all (pre-#30).
      kind: list.kind === 'cart' ? 'cart' : 'wishlist',
      items: Array.isArray(list.items) ? list.items.map(normalizeItem) : [],
      // sharing is a UI-facing mirror of shareToken (true once a token
      // exists), kept as its own field only so the "turning on sharing..."
      // in-between state -- after clicking Share but before the save
      // round trip returns a real token -- has somewhere to live. Every
      // real save response resets it back to !!shareToken (see saveToServer).
      sharing: !!list.shareToken,
      shareToken: list.shareToken || null,
    }));

    // Defensive only -- the worker enforces "exactly one primary list with
    // id 'wishlist'" server-side, so this should never actually trigger
    // against real saved data. Guards against a corrupt localStorage blob.
    if (!normalized.some(list => list.primary && list.id === PRIMARY_LIST_ID)) {
      normalized.unshift({ id: PRIMARY_LIST_ID, name: PRIMARY_LIST_NAME, primary: true, kind: 'wishlist', items: [], sharing: false, shareToken: null });
    }

    return normalized;
  }

  // ─── LOAD ──────────────────────────────────────────────────────────────────
  function loadFromLocalStorage() {
    try {
      return normalizeLists(JSON.parse(localStorage.getItem(STORAGE_KEY)));
    } catch {
      return normalizeLists([]);
    }
  }

  // A logged-in customer's lists live in Postgres. The page used to render the
  // customer metafield into its HTML instead, but nothing has written that
  // since storage moved to Postgres, so it was a frozen snapshot: deleted lists
  // came back on refresh and the next save wrote them back (backbone-wishlist-
  // worker#34). Returns the normalized lists, or null when they could not be
  // loaded. Callers must treat null as "unknown", never as "empty": every save
  // posts the WHOLE state and would overwrite what is really stored.
  async function loadFromServer() {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const response = await fetch('/apps/wishlist/lists', {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        const result = await response.json();
        if (response.ok && result.success && Array.isArray(result.wishlist)) {
          return normalizeLists(result.wishlist);
        }
      } catch (err) {
        console.error('Backbone Wishlist: Failed to load lists', err);
      }
      if (attempt === 0) await new Promise(resolve => setTimeout(resolve, 500));
    }
    return null;
  }

  // ─── SAVE ──────────────────────────────────────────────────────────────────
  // Every item carries a variantId in memory (see normalizeItem) so
  // comparisons stay simple, but it's null on every item until variant-level
  // saves are built (issue #9) -- storing "variantId": null on every single
  // item is pure dead weight against Shopify's 128KB JSON metafield limit,
  // so it's dropped here, at the point data actually gets persisted.
  function serializeForStorage(listsToSerialize) {
    return listsToSerialize.map(list => ({
      id: list.id,
      name: list.name,
      primary: list.primary,
      // Omitted entirely for a wishlist-kind list (the default) -- same
      // zero-migration-cost reasoning as the worker's own serializeForStorage.
      ...(list.kind === 'cart' ? { kind: 'cart' } : {}),
      items: list.items.map(item => ({
        productId: item.productId,
        ...(item.variantId ? { variantId: item.variantId } : {}),
        ...(typeof item.quantity === 'number' ? { quantity: item.quantity } : {}),
      })),
      // sharing is the worker's real signal for "mint/keep a token vs.
      // clear it" -- see parseWishlist in backbone-wishlist-worker. The
      // token itself is echoed back (when known) so a routine save
      // doesn't rotate a link that's already been shared out.
      sharing: !!list.sharing,
      ...(list.shareToken ? { shareToken: list.shareToken } : {}),
    }));
  }

  async function saveWishlist() {
    if (IS_LOGGED_IN) {
      if (listsLoadFailed) {
        // Never save over lists we couldn't read -- the save replaces the whole
        // stored set (see loadFromServer).
        showToast("We couldn't load your saved lists, so this change wasn't saved. Please refresh the page.");
        return;
      }
      await saveToServer();
    } else {
      saveToLocalStorage();
    }
    updateBadge();
  }

  function saveToLocalStorage() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(serializeForStorage(lists)));
  }

  async function saveToServer() {
    // Posts the whole in-memory state to the Backbone Wishlist app's App Proxy
    // (same-origin, no token needed here at all -- Shopify signs the forwarded
    // request and identifies the customer server-side via
    // logged_in_customer_id). The worker validates it and the proxy stores it
    // in Postgres. See backbone-wishlist-worker and backbone-customer-account-proxy.
    try {
      const response = await fetch('/apps/wishlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wishlist: serializeForStorage(lists) }),
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        console.error('Backbone Wishlist: Save failed', result);
        showToast("Couldn't save your wishlist. Please try again.");
        return;
      }

      // The worker is the only place a newly minted share token exists --
      // adopt its response as the new canonical state so a "Share" click
      // picks up the real token (and `sharing` gets reconciled back to
      // !!shareToken, clearing the optimistic in-between state).
      if (result.wishlist) {
        lists = normalizeLists(result.wishlist);
      }
    } catch (err) {
      console.error('Backbone Wishlist: Network error', err);
      showToast("Couldn't save your wishlist. Please try again.");
    }
  }

  // ─── STOREFRONT API HELPER ─────────────────────────────────────────────────
  async function storefrontFetch(query, variables) {
    const response = await fetch(`https://${SHOP_DOMAIN}/api/${API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query, variables }),
    });
    return response.json();
  }

  function variantIdFromGid(gid) {
    return gid.replace('gid://shopify/ProductVariant/', '');
  }

  // ─── CART ──────────────────────────────────────────────────────────────────
  // The theme's cart UI (header badge, cart drawer) is entirely event-driven
  // -- components listen on `document` for the shared CartLinesUpdateEvent
  // (see @shopify/events, imported via the theme's importmap) rather than
  // exposing a DOM element with a renderContents()-style API. Same event
  // shape assets/product-form.js dispatches, so this reaches the same
  // listeners (cart-icon, cart-drawer) a normal product-page add does -- a
  // wishlist add shouldn't feel like a different, lesser path through the
  // store. Dynamically imported since this file loads as a classic script,
  // not a module.
  async function dispatchCartLinesUpdate(lines) {
    const { CartLinesUpdateEvent } = await import('@shopify/events');
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({ action: 'add', context: 'wishlist', lines, promise: deferred.promise })
    );

    try {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const ajaxCart = await response.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: { items: ajaxCart.items, source: 'wishlist', itemCount: ajaxCart.item_count, didError: false },
      });
    } catch (err) {
      deferred.reject(err);
    }
  }

  async function addVariantsToCart(items) {
    if (items.length === 0) return { success: false, error: 'Nothing to add' };

    const lines = items.map(item => ({ merchandiseId: item.id, quantity: item.quantity }));

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const result = await response.json();

      if (!response.ok) {
        // Shopify can genuinely add *some* of a multi-item request and
        // still respond with a non-2xx status and an `errors` message
        // like "Only 1 item was added to your cart due to availability"
        // -- real-time inventory can shift between page load (when
        // availability was last checked) and the click. That's a partial
        // outcome, not a clean failure, and the items that did get added
        // are real, so the cart UI still needs to refresh to reflect them.
        const message = typeof result.errors === 'string' ? result.errors : (result.description || result.message || "Couldn't add to cart");
        await dispatchCartLinesUpdate(lines);
        return { success: false, partial: true, error: message };
      }

      await dispatchCartLinesUpdate(lines);
      return { success: true };
    } catch (err) {
      console.error('Backbone Wishlist: Add to cart failed', err);
      return { success: false, error: "Couldn't add to cart" };
    }
  }

  // "Cart saved Aug 3" -- the default name when the save prompt is skipped
  // (backbonecraftco-com#30).
  function formatShortDate(date) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  // Saved Carts (#30): snapshots the customer's live cart into a new
  // cart-kind list. Logged-in only -- createList() already enforces that
  // (guests never get past the primary wishlist), so this is the one other
  // place that same rule needs to hold, checked here too for a clear error
  // instead of a silent no-op if this is ever reachable while logged out.
  async function saveCurrentCartAsList(name) {
    if (!IS_LOGGED_IN) return { success: false, error: 'Log in to save this cart.' };

    let cart;
    try {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      cart = await response.json();
    } catch (err) {
      console.error('Backbone Wishlist: Failed to read cart', err);
      return { success: false, error: "Couldn't read your cart. Please try again." };
    }

    const lines = cart.items || [];
    if (lines.length === 0) return { success: false, error: 'Your cart is empty.' };

    // Subscription lines excluded from v1 (decided 2026-09-01, #30) --
    // restoring a subscription selection later raises its own questions
    // (re-check plan availability/pricing at restore time?) that don't
    // need solving on day one. selling_plan_allocation is only ever present
    // on a subscribed line, same signal the Ajax cart API uses elsewhere.
    const eligibleLines = lines.filter(line => !line.selling_plan_allocation);
    if (eligibleLines.length === 0) {
      return { success: false, error: "This cart is subscription items only, which can't be saved yet." };
    }

    const trimmedName = (name || '').trim() || `Cart saved ${formatShortDate(new Date())}`;
    const list = createList(trimmedName, 'cart');
    if (!list) return { success: false, error: "Couldn't save this cart." };

    list.items = eligibleLines.map(line => ({
      productId: String(line.product_id),
      variantId: String(line.variant_id),
      quantity: line.quantity,
    }));

    activeListId = list.id;
    await saveWishlist();
    return { success: true, list, skipped: lines.length - eligibleLines.length };
  }

  // ─── LIST / ITEM HELPERS ───────────────────────────────────────────────────
  function getPrimaryList() {
    return lists.find(list => list.primary) || lists[0];
  }

  function findList(listId) {
    return lists.find(list => list.id === listId);
  }

  function generateListId() {
    let id;
    do {
      id = Math.random().toString(36).slice(2, 8);
    } while (id === PRIMARY_LIST_ID || findList(id));
    return id;
  }

  function itemMatches(item, productId, variantId) {
    if (item.productId !== String(productId)) return false;
    if (variantId === undefined) return true;
    return item.variantId === variantId;
  }

  function isInList(listId, productId, variantId) {
    const list = findList(listId);
    if (!list) return false;
    return list.items.some(item => itemMatches(item, productId, variantId));
  }

  // Used by the heart everywhere (collection cards + product page quick-add)
  // -- hearts only ever reflect/target the primary list, per the confirmed
  // UX: one-click save goes to "Wishlist," picking a different list is a
  // deliberate action via the list picker on the product page. variantId
  // is null on a collection card (no variant context there) or a
  // single-variant product page, or a real id on a product page with an
  // actual picker -- see getSelectedVariantId.
  function isInWishlist(productId, variantId = null) {
    return isInList(PRIMARY_LIST_ID, productId, variantId);
  }

  // Every list (primary or custom) that currently holds this exact
  // product+variant -- used only for the product-page label text, so a
  // customer who saved something to a custom list only (not the primary)
  // isn't told nothing's saved. The heart itself still only reflects/
  // targets the primary list.
  function listsContaining(productId, variantId) {
    return lists.filter(list => list.items.some(item => itemMatches(item, productId, variantId)));
  }

  // ─── MUTATIONS (state only -- callers handle save + re-render) ────────────
  function toggleItem(productId, listId = PRIMARY_LIST_ID, variantId = null) {
    const list = findList(listId) || getPrimaryList();
    const index = list.items.findIndex(item => itemMatches(item, productId, variantId));

    if (index === -1) {
      list.items.push({ productId: String(productId), variantId });
      return { added: true, list };
    }
    list.items.splice(index, 1);
    return { added: false, list };
  }

  function addItemToList(productId, listId, variantId = null) {
    const list = findList(listId);
    if (!list) return false;
    if (isInList(listId, productId, variantId)) return false;
    list.items.push({ productId: String(productId), variantId });
    return true;
  }

  function removeItemFromList(productId, listId, variantId = null) {
    const list = findList(listId);
    if (!list) return false;
    const index = list.items.findIndex(item => itemMatches(item, productId, variantId));
    if (index === -1) return false;
    list.items.splice(index, 1);
    return true;
  }

  function moveItem(productId, fromListId, toListId, variantId = null) {
    const from = findList(fromListId);
    const to = findList(toListId);
    if (!from || !to || fromListId === toListId) return false;

    const index = from.items.findIndex(item => itemMatches(item, productId, variantId));
    if (index === -1) return false;

    const [item] = from.items.splice(index, 1);
    if (!to.items.some(existing => existing.productId === item.productId && existing.variantId === item.variantId)) {
      to.items.push(item);
    }
    return true;
  }

  // Guests are limited to the single primary list -- no localStorage-only
  // custom lists, no renaming "Wishlist" into something else. Multi-list
  // management only makes sense once there's an account to persist it to.
  function createList(name, kind = 'wishlist') {
    if (!IS_LOGGED_IN) return null;
    const trimmed = (name || '').trim();
    if (!trimmed) return null;
    const list = { id: generateListId(), name: trimmed, primary: false, kind, items: [], sharing: false, shareToken: null };
    lists.push(list);
    return list;
  }

  function renameList(listId, name) {
    if (!IS_LOGGED_IN) return false;
    const list = findList(listId);
    const trimmed = (name || '').trim();
    if (!list || !trimmed) return false;
    list.name = trimmed;
    return true;
  }

  // Primary list can never be deleted -- checked here even though the UI
  // never renders a delete control for it, and the worker rejects it too,
  // since this function is the one place all three layers ultimately rely
  // on to actually not do it.
  function deleteList(listId, { moveItemsTo } = {}) {
    const list = findList(listId);
    if (!list || list.primary) return false;

    if (moveItemsTo && list.items.length) {
      const target = findList(moveItemsTo);
      if (target) {
        list.items.forEach(item => {
          if (!target.items.some(existing => existing.productId === item.productId && existing.variantId === item.variantId)) {
            target.items.push(item);
          }
        });
      }
    }

    lists = lists.filter(l => l.id !== listId);
    return true;
  }

  // Only sets local intent -- the real token only ever comes from the
  // worker's save response (see saveToServer), since it has to be
  // minted somewhere that can't be spoofed by the client.
  function setListSharing(listId, enabled) {
    const list = findList(listId);
    if (!list) return false;
    list.sharing = enabled;
    if (!enabled) list.shareToken = null;
    return true;
  }

  function buildShareUrl(list) {
    const url = new URL('/pages/wishlist', window.location.origin);
    url.searchParams.set('share_customer', CUSTOMER_ID);
    url.searchParams.set('share_list', list.id);
    url.searchParams.set('share_token', list.shareToken);
    return url.toString();
  }

  // ─── RENDER: HEARTS ─────────────────────────────────────────────────────────
  // The product page's own heart uses the live-tracked selection
  // (currentProductVariantId, kept in sync as the customer switches
  // options -- see bindVariantChangeListener). Everywhere else (collection
  // cards) there's no picker to track live, so it falls back to whatever
  // real variant wishlist-heart.liquid resolved and rendered server-side
  // (the product's default/first-available one) -- null only ever remains
  // for a genuinely single-variant product. This is what keeps a
  // multi-variant product from ever being saved as the ambiguous bare
  // "parent" item, on either surface.
  function getHeartVariantId(btn) {
    return btn.closest('.product-wishlist') ? currentProductVariantId : (btn.dataset.variantId || null);
  }

  function renderHearts() {
    document.querySelectorAll('.wishlist-heart').forEach(btn => {
      const id = btn.dataset.productId;
      const variantId = getHeartVariantId(btn);
      const active = isInWishlist(id, variantId);
      btn.classList.toggle('wishlist-heart--active', active);
      const productTitle = btn.dataset.productTitle;
      const heartAriaLabel = productTitle
        ? `${active ? 'Remove' : 'Add'} ${productTitle} ${active ? 'from' : 'to'} wishlist`
        : (active ? 'Remove from wishlist' : 'Add to wishlist');
      btn.setAttribute('aria-label', heartAriaLabel);

      const outlineIcon = btn.querySelector('.wishlist-heart__icon--outline');
      const filledIcon = btn.querySelector('.wishlist-heart__icon--filled');
      if (outlineIcon) outlineIcon.hidden = active;
      if (filledIcon) filledIcon.hidden = !active;

      const label = btn.parentElement?.querySelector('.product-wishlist__label');
      if (label) {
        const otherLists = listsContaining(id, variantId).filter(list => !list.primary);
        if (active && otherLists.length) {
          const othersLabel = otherLists.length === 1 ? '1 other' : `${otherLists.length} others`;
          const primaryName = getPrimaryList()?.name || 'Wishlist';
          label.innerHTML = `Added to your ${listLink(PRIMARY_LIST_ID, primaryName)} +${othersLabel}`;
          label.title = otherLists.map(list => list.name).join(', ');
        } else if (active) {
          const primaryName = getPrimaryList()?.name || 'Wishlist';
          label.innerHTML = `Added to your ${listLink(PRIMARY_LIST_ID, primaryName)}`;
          label.removeAttribute('title');
        } else if (otherLists.length) {
          const firstList = otherLists[0];
          const remaining = otherLists.length - 1;
          label.innerHTML = remaining
            ? `Saved to ${listLink(firstList.id, firstList.name)} +${remaining === 1 ? '1 other' : `${remaining} others`}`
            : `Saved to ${listLink(firstList.id, firstList.name)}`;
          label.title = otherLists.map(list => list.name).join(', ');
        } else {
          label.textContent = 'Save to Wishlist';
          label.removeAttribute('title');
        }
      }
    });
  }

  function updateBadge() {
    const badge = document.querySelector('.wishlist-count-bubble');
    if (!badge) return;
    const count = getPrimaryList()?.items.length || 0;
    const countEl = badge.querySelector('.wishlist-count-bubble__count');
    if (countEl) countEl.textContent = count > 0 ? count : '';
    badge.hidden = count === 0;
  }

  // ─── RENDER: PRODUCT-PAGE LIST PICKER ──────────────────────────────────────
  function renderListPickers() {
    document.querySelectorAll('.wishlist-list-picker').forEach(picker => {
      const productId = picker.dataset.productId;
      const listEl = picker.querySelector('.wishlist-list-picker__lists');
      if (!listEl) return;

      listEl.innerHTML = lists.map(list => {
        // The picker only ever renders on the product page, so it always
        // has variant context (null on a single-variant product, same as
        // the heart there).
        const active = isInList(list.id, productId, currentProductVariantId);
        return `
          <li>
            <button
              type="button"
              class="wishlist-list-picker__item${active ? ' wishlist-list-picker__item--active' : ''}"
              data-list-id="${escapeHtml(list.id)}"
              data-product-id="${escapeHtml(productId)}"
              aria-pressed="${active}"
            >
              <span class="wishlist-list-picker__checkbox" aria-hidden="true"></span>
              ${escapeHtml(list.name)}
            </button>
          </li>
        `;
      }).join('');

      picker.hidden = false;
    });
  }

  function closeAllListPickers() {
    document.querySelectorAll('.wishlist-list-picker__menu').forEach(menu => { menu.hidden = true; });
    document.querySelectorAll('.wishlist-list-picker__trigger').forEach(trigger => {
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  function getWishlistPageContainer() {
    return document.getElementById('wishlist-products');
  }

  function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value ?? '';
    return div.innerHTML;
  }

  // Links a list name (in the "Added to your Wishlist" / "Saved to X"
  // labels) to that specific list on the wishlist page -- read back by
  // init() via the ?list= param.
  function listLink(listId, listName) {
    const url = `/pages/wishlist?list=${encodeURIComponent(listId)}`;
    return `<a href="${url}">${escapeHtml(listName)}</a>`;
  }

  // ─── RENDER: WISHLIST PAGE ──────────────────────────────────────────────────
  async function renderWishlistPage() {
    const container = getWishlistPageContainer();
    if (!container) return;

    if (!findList(activeListId)) activeListId = PRIMARY_LIST_ID;
    const activeList = findList(activeListId);

    container.innerHTML = `
      <div class="wishlist-manager">
        ${renderNotifyPreferenceMarkup()}
        ${renderTabsMarkup()}
        ${pendingDeleteListId ? renderDeleteConfirmMarkup(findList(pendingDeleteListId)) : renderListHeaderMarkup(activeList)}
        <div class="wishlist-products-grid" id="wishlist-active-list-items"></div>
      </div>
    `;

    if (pendingDeleteListId) return; // grid stays empty behind the confirm panel

    const itemsContainer = container.querySelector('#wishlist-active-list-items');
    await renderListItems(activeList, itemsContainer);
  }

  // Global toggle, not per-list or per-item -- deliberately simple
  // ("notify me about my wishlist," not a control on every saved item).
  // Guests get nothing here: there's no Shopify customer id to resolve to
  // a Klaviyo profile for them, and the whole point is alerts reaching
  // them somewhere, which requires being a real logged-in customer.
  function renderNotifyPreferenceMarkup() {
    if (!IS_LOGGED_IN) return '';
    return `
      <label class="wishlist-notify-preference">
        <input type="checkbox" class="wishlist-notify-toggle" ${notifyPreferenceEnabled ? 'checked' : ''}>
        Notify me by email or text when a saved item is back in stock or drops in price
      </label>
    `;
  }

  // Saved Carts render as their own row, below the wishlist tabs -- "a
  // clearly separate 'Saved Carts' section/tab, matching the different
  // customer intent (reorder vs. wishlist)" (#30, decided 2026-09-01).
  // Same tab markup/behavior either way (bindEvents' .wishlist-tabs__tab
  // handler doesn't care which row it came from) -- only the grouping and
  // the accent color (see .wishlist-tabs__tab--cart in component-wishlist.css)
  // differ.
  function renderTab(list) {
    return `
      <button
        type="button"
        class="wishlist-tabs__tab${list.id === activeListId ? ' wishlist-tabs__tab--active' : ''}${list.kind === 'cart' ? ' wishlist-tabs__tab--cart' : ''}"
        data-list-id="${escapeHtml(list.id)}"
        role="tab"
        aria-selected="${list.id === activeListId}"
      >
        ${escapeHtml(list.name)}
        <span class="wishlist-tabs__count">${list.items.length}</span>
      </button>
    `;
  }

  function renderTabsMarkup() {
    const wishlistTabs = lists.filter(list => list.kind !== 'cart');
    const cartTabs = lists.filter(list => list.kind === 'cart');

    const newListBtn = IS_LOGGED_IN
      ? `<button type="button" class="wishlist-tabs__new">+ New list</button>`
      : '';

    // Saved Carts are only ever created from the cart page/drawer's "Save
    // this cart" button, never from here -- so this row only renders once
    // at least one exists, with no "+" control of its own.
    const cartSection = cartTabs.length ? `
      <div class="wishlist-tabs wishlist-tabs--carts" role="tablist" aria-label="Saved Carts">
        <span class="wishlist-tabs__group-label">Saved Carts</span>
        ${cartTabs.map(renderTab).join('')}
      </div>
    ` : '';

    return `
      <div class="wishlist-tabs" role="tablist" aria-label="Wishlists">
        ${wishlistTabs.map(renderTab).join('')}
        ${newListBtn}
      </div>
      ${cartSection}
    `;
  }

  function renderListHeaderMarkup(list) {
    if (!list) return '';
    const isCart = list.kind === 'cart';
    // Sharing isn't supported on a cart-kind list yet (deferred to #31,
    // rejected server-side by the worker's parseWishlist) -- never render
    // a control that would produce a save the worker turns right around
    // and rejects.
    const shareEnableBtn = IS_LOGGED_IN && !isCart && !list.sharing
      ? `<button type="button" class="wishlist-list-header__share-enable" data-list-id="${escapeHtml(list.id)}">Share</button>`
      : '';

    const addAllBtn = list.items.length > 0
      ? `<button type="button" class="wishlist-list-header__add-all">${isCart ? 'Restore cart' : 'Add all to cart'}</button>`
      : '';

    const renameBtn = IS_LOGGED_IN
      ? `<button type="button" class="wishlist-list-header__rename" data-list-id="${escapeHtml(list.id)}">Rename</button>`
      : '';

    return `
      <div class="wishlist-list-header${isCart ? ' wishlist-list-header--cart' : ''}">
        <h2 class="wishlist-list-header__name">${escapeHtml(list.name)}</h2>
        <div class="wishlist-list-header__actions">
          ${addAllBtn}
          ${renameBtn}
          ${shareEnableBtn}
          ${IS_LOGGED_IN && !list.primary ? `<button type="button" class="wishlist-list-header__delete" data-list-id="${escapeHtml(list.id)}">Delete list</button>` : ''}
        </div>
      </div>
      ${IS_LOGGED_IN && !isCart && list.sharing ? renderSharePanelMarkup(list) : ''}
      ${!IS_LOGGED_IN && !isCart ? '<p class="wishlist-share-hint">Log in to share this list.</p>' : ''}
    `;
  }

  function renderSharePanelMarkup(list) {
    if (!list.shareToken) {
      return '<div class="wishlist-share-panel"><p>Turning on sharing…</p></div>';
    }

    const url = buildShareUrl(list);
    return `
      <div class="wishlist-share-panel">
        <p>Anyone with this link can view "${escapeHtml(list.name)}" -- no account details are shown, and they can't make changes.</p>
        <div class="wishlist-share-panel__row">
          <input type="text" class="wishlist-share-panel__link" value="${escapeHtml(url)}" readonly>
          <button type="button" class="wishlist-share-panel__copy" data-url="${escapeHtml(url)}">Copy link</button>
        </div>
        <button type="button" class="wishlist-share-panel__disable" data-list-id="${escapeHtml(list.id)}">Turn off sharing</button>
      </div>
    `;
  }

  function renderDeleteConfirmMarkup(list) {
    if (!list) return '';
    const otherLists = lists.filter(l => l.id !== list.id);
    const options = otherLists.map(l => `<option value="${escapeHtml(l.id)}">${escapeHtml(l.name)}</option>`).join('');

    return `
      <div class="wishlist-delete-confirm">
        <p>
          "<strong>${escapeHtml(list.name)}</strong>" still has ${list.items.length}
          item${list.items.length === 1 ? '' : 's'} saved. What would you like to do?
        </p>
        <div class="wishlist-delete-confirm__row">
          <select class="wishlist-delete-confirm__target">
            <option value="">Choose a list to move items to…</option>
            ${options}
          </select>
          <button type="button" class="wishlist-delete-confirm__move-delete" disabled>Move items &amp; delete list</button>
        </div>
        <div class="wishlist-delete-confirm__row">
          <button type="button" class="wishlist-delete-confirm__delete-anyway">Delete list without moving items</button>
          <button type="button" class="wishlist-delete-confirm__cancel">Cancel</button>
        </div>
      </div>
    `;
  }

  // Renders a single list's saved products by looking them up in bulk via
  // the Storefront API. readOnly drops the move/remove controls -- used
  // by the public shared-list view (see renderSharedListView), where the
  // visitor isn't the list's owner and shouldn't be able to change it.
  async function renderListItems(list, container, { readOnly = false } = {}) {
    if (!container) return;

    if (!list || list.items.length === 0) {
      container.innerHTML = emptyStateMarkup(list, readOnly);
      return;
    }

    if (!STOREFRONT_TOKEN || !SHOP_DOMAIN) {
      container.innerHTML = '<p class="wishlist-error">Unable to load wishlist right now. Please try again.</p>';
      return;
    }

    container.innerHTML = '<p class="wishlist-loading">Loading your wishlist...</p>';

    // Mixed Product/ProductVariant lookup in one request: an item saved
    // with a specific variant (post-#9) queries that ProductVariant
    // directly for its own title/price/image; a legacy item (variantId
    // null, saved before #9 or from a collection-card heart) queries the
    // Product instead and falls back to its default/first-available
    // variant, same as before #9. nodes() preserves input order, so each
    // result lines up with list.items by index -- no id round-tripping
    // needed to re-associate them.
    const ids = list.items.map(item => item.variantId
      ? `gid://shopify/ProductVariant/${item.variantId}`
      : `gid://shopify/Product/${item.productId}`
    );

    const query = `
      query getWishlistItems($ids: [ID!]!) {
        nodes(ids: $ids) {
          __typename
          ... on Product {
            title
            handle
            featuredImage {
              url
              altText
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            selectedOrFirstAvailableVariant {
              id
              availableForSale
            }
          }
          ... on ProductVariant {
            id
            title
            availableForSale
            price {
              amount
              currencyCode
            }
            image {
              url
              altText
            }
            product {
              title
              handle
              featuredImage {
                url
                altText
              }
            }
          }
        }
      }
    `;

    try {
      const response = await storefrontFetch(query, { ids });
      const nodes = response.data?.nodes || [];

      // Pair by position (not id) since a variant node has no bare
      // productId of its own to match back against -- item.productId is
      // already known locally, no need to ask the API for it.
      const displayItems = list.items
        .map((item, index) => normalizeDisplayItem(item, nodes[index]))
        .filter(Boolean);

      if (displayItems.length === 0) {
        container.innerHTML = emptyStateMarkup(list, readOnly);
        activeListAddableItems = [];
        return;
      }

      const otherLists = readOnly ? [] : lists.filter(l => l.id !== list.id);

      // Stashed for the per-list "Add all to cart"/"Restore cart" bulk
      // action -- it needs to know which items currently have a purchasable
      // variant without re-fetching, and re-deriving it from `list.items`
      // alone can't tell sold-out apart from in-stock. quantity defaults to
      // 1 for a plain wishlist item (no quantity field at all); a cart-kind
      // item always carries a real one (enforced by the worker's isValidItem).
      activeListAddableItems = displayItems
        .filter(d => d.available)
        .map(d => ({ variantId: d.cartVariantId, quantity: d.quantity || 1 }));

      const isCart = list.kind === 'cart';

      container.innerHTML = displayItems.map(d => {
        const moveOptions = otherLists.map(l => `<option value="${escapeHtml(l.id)}">${escapeHtml(l.name)}</option>`).join('');
        const variantAttr = d.savedVariantId ? ` data-variant-id="${escapeHtml(d.savedVariantId)}"` : '';
        const quantity = d.quantity || 1;

        // Restoring at a saved quantity > 1 is worth surfacing on the
        // button itself, not just the (easy to miss) quantity field next
        // to it -- "Add 3 to Cart" makes the restore concrete before the
        // click, not just after.
        const addToCartLabel = d.available ? (isCart && quantity > 1 ? `Add ${quantity} to Cart` : 'Add to Cart') : 'Sold out';

        return `
          <div class="wishlist-card${isCart ? ' wishlist-card--cart' : ''}" data-product-id="${escapeHtml(d.productId)}"${variantAttr}>
            <a href="/products/${d.handle}" class="wishlist-card__image-link">
              ${d.image
                ? `<img src="${d.image.url}" alt="${escapeHtml(d.image.altText || d.title)}" loading="lazy">`
                : '<div class="wishlist-card__image-placeholder"></div>'
              }
            </a>
            <div class="wishlist-card__info">
              <a href="/products/${d.handle}" class="wishlist-card__title">${escapeHtml(d.title)}</a>
              <p class="wishlist-card__price">$${d.price} ${d.currency}</p>
              ${isCart && !readOnly ? `
                <label class="wishlist-card__qty-label">
                  Qty
                  <input
                    type="number"
                    class="wishlist-card__quantity"
                    min="1"
                    max="999"
                    value="${quantity}"
                    data-product-id="${escapeHtml(d.productId)}"
                    data-list-id="${escapeHtml(list.id)}"${variantAttr}
                  >
                </label>
              ` : ''}
            </div>
            <div class="wishlist-card__actions">
              ${d.cartVariantId ? `
                <button class="wishlist-card__add-to-cart" data-variant-id="${escapeHtml(d.cartVariantId)}" data-quantity="${quantity}" ${d.available ? '' : 'disabled'}>
                  ${addToCartLabel}
                </button>
              ` : ''}
              ${readOnly ? '' : `
                ${otherLists.length ? `
                  <select class="wishlist-card__move-to" data-product-id="${escapeHtml(d.productId)}" data-list-id="${escapeHtml(list.id)}"${variantAttr}>
                    <option value="">Move to…</option>
                    ${moveOptions}
                  </select>
                ` : ''}
                <button class="wishlist-card__remove" data-product-id="${escapeHtml(d.productId)}" data-list-id="${escapeHtml(list.id)}"${variantAttr} aria-label="Remove from ${escapeHtml(list.name)}">
                  Remove
                </button>
              `}
            </div>
          </div>
        `;
      }).join('');

    } catch (err) {
      console.error('Backbone Wishlist: Failed to load products', err);
      container.innerHTML = '<p class="wishlist-error">Unable to load wishlist right now. Please try again.</p>';
    }
  }

  // Reduces either API shape (a specific ProductVariant, or a Product
  // for legacy/variant-less items) down to one common shape the card
  // renderer doesn't need to know the difference for. Returns null for a
  // node Shopify couldn't resolve at all (product or variant deleted).
  function normalizeDisplayItem(item, node) {
    if (!node) return null;

    if (node.__typename === 'ProductVariant') {
      const variantId = variantIdFromGid(node.id);
      const image = node.image || node.product.featuredImage;
      return {
        productId: item.productId,
        savedVariantId: variantId,
        cartVariantId: variantId,
        handle: node.product.handle,
        title: `${node.product.title} — ${node.title}`,
        price: parseFloat(node.price.amount).toFixed(2),
        currency: node.price.currencyCode,
        image,
        available: !!node.availableForSale,
        quantity: item.quantity,
      };
    }

    // Legacy / variant-less item -- product-level display, falls back to
    // the default/first-available variant for add-to-cart, same as
    // pre-#9 behavior.
    const fallbackVariant = node.selectedOrFirstAvailableVariant;
    return {
      productId: item.productId,
      savedVariantId: null,
      cartVariantId: fallbackVariant ? variantIdFromGid(fallbackVariant.id) : null,
      handle: node.handle,
      title: node.title,
      price: parseFloat(node.priceRange.minVariantPrice.amount).toFixed(2),
      currency: node.priceRange.minVariantPrice.currencyCode,
      image: node.featuredImage,
      available: !!fallbackVariant?.availableForSale,
      quantity: item.quantity,
    };
  }

  function emptyStateMarkup(list, readOnly = false) {
    const isCart = list?.kind === 'cart';
    const label = readOnly
      ? `"${escapeHtml(list?.name || 'This list')}" doesn't have anything saved yet.`
      : isCart
        ? `"${escapeHtml(list.name)}" doesn't have anything saved -- restoring it removed everything from the cart.`
        : (list && !list.primary ? `"${escapeHtml(list.name)}" is empty.` : 'Nothing saved yet. Start browsing.');
    return `
      <div class="wishlist-empty">
        <p>${label}</p>
        <a href="${ALL_PRODUCTS_URL}" class="button button--primary">Shop All</a>
      </div>
    `;
  }

  // ─── SHARED (PUBLIC, READ-ONLY) LIST VIEW ──────────────────────────────────
  function getShareParamsFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const customer = params.get('share_customer');
    const list = params.get('share_list');
    const token = params.get('share_token');
    if (!customer || !list || !token) return null;
    return { customer, list, token };
  }

  async function renderSharedListView({ customer, list, token }) {
    const container = getWishlistPageContainer();
    if (!container) return;

    // The normal page's static Liquid subtitle ("Saved items sync across
    // your devices" / "Log in to save...") doesn't apply to a visitor
    // looking at someone else's shared list -- nothing server-side knows
    // this is a share link (see main-page-wishlist.liquid), so it's
    // swapped out here instead.
    const subtitle = document.querySelector('.wishlist-page__subtitle');
    if (subtitle) subtitle.textContent = "You're viewing a shared wishlist.";

    container.innerHTML = '<p class="wishlist-loading">Loading shared list...</p>';

    try {
      const params = new URLSearchParams({ customer, list, token });
      const response = await fetch(`/apps/wishlist/shared?${params.toString()}`);
      const result = await response.json();

      if (!response.ok || !result.success) {
        container.innerHTML = '<p class="wishlist-error">This shared list is no longer available.</p>';
        return;
      }

      const heading = document.createElement('h2');
      heading.className = 'wishlist-list-header__name';
      heading.textContent = result.list.name;
      container.innerHTML = '';
      container.appendChild(heading);

      const grid = document.createElement('div');
      grid.className = 'wishlist-products-grid';
      container.appendChild(grid);

      await renderListItems(result.list, grid, { readOnly: true });
    } catch (err) {
      console.error('Backbone Wishlist: Failed to load shared list', err);
      container.innerHTML = '<p class="wishlist-error">This shared list is no longer available.</p>';
    }
  }

  // ─── TOAST ─────────────────────────────────────────────────────────────────
  function showToast(message) {
    let toast = document.getElementById('wishlist-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'wishlist-toast';
      toast.className = 'wishlist-toast';
      document.body.appendChild(toast);
    }
    toast.innerHTML = message;
    toast.classList.add('wishlist-toast--visible');
    clearTimeout(toast._timeout);
    toast._timeout = setTimeout(() => {
      toast.classList.remove('wishlist-toast--visible');
    }, 3500);
  }

  // ─── EVENTS ────────────────────────────────────────────────────────────────
  function bindEvents() {
    document.addEventListener('click', async e => {
      const heart = e.target.closest('.wishlist-heart');
      if (heart) {
        e.preventDefault();
        const { added } = toggleItem(heart.dataset.productId, PRIMARY_LIST_ID, getHeartVariantId(heart));
        showToast(
          added
            ? (IS_LOGGED_IN ? 'Added to your wishlist.' : `Added to wishlist. <a href="${LOGIN_URL}">Log in</a> to save across devices.`)
            : 'Removed from your wishlist.'
        );
        renderHearts();
        renderListPickers();
        await saveWishlist();
        if (getWishlistPageContainer()) await renderWishlistPage();
        return;
      }

      const pickerTrigger = e.target.closest('.wishlist-list-picker__trigger');
      if (pickerTrigger) {
        e.preventDefault();
        const menu = pickerTrigger.parentElement.querySelector('.wishlist-list-picker__menu');
        const willOpen = menu.hidden;
        closeAllListPickers();
        if (willOpen) {
          menu.hidden = false;
          pickerTrigger.setAttribute('aria-expanded', 'true');
        }
        return;
      }

      const pickerItem = e.target.closest('.wishlist-list-picker__item');
      if (pickerItem) {
        e.preventDefault();
        const { productId, listId } = pickerItem.dataset;
        if (isInList(listId, productId, currentProductVariantId)) {
          removeItemFromList(productId, listId, currentProductVariantId);
        } else {
          addItemToList(productId, listId, currentProductVariantId);
        }
        renderHearts();
        renderListPickers();
        await saveWishlist();
        if (getWishlistPageContainer()) await renderWishlistPage();
        return;
      }

      const newListFromPicker = e.target.closest('.wishlist-list-picker__new-list');
      if (newListFromPicker) {
        e.preventDefault();
        const picker = newListFromPicker.closest('.wishlist-list-picker');
        const name = window.prompt('Name your new list:');
        const list = createList(name);
        if (list) {
          addItemToList(picker.dataset.productId, list.id, currentProductVariantId);
          renderHearts();
          renderListPickers();
          await saveWishlist();
          if (getWishlistPageContainer()) await renderWishlistPage();
        }
        return;
      }

      if (!e.target.closest('.wishlist-list-picker')) {
        closeAllListPickers();
      }

      const tab = e.target.closest('.wishlist-tabs__tab');
      if (tab) {
        activeListId = tab.dataset.listId;
        pendingDeleteListId = null;
        await renderWishlistPage();
        return;
      }

      const newListTab = e.target.closest('.wishlist-tabs__new');
      if (newListTab) {
        const name = window.prompt('Name your new list:');
        const list = createList(name);
        if (list) {
          activeListId = list.id;
          renderListPickers();
          await saveWishlist();
          await renderWishlistPage();
        }
        return;
      }

      const renameBtn = e.target.closest('.wishlist-list-header__rename');
      if (renameBtn) {
        const list = findList(renameBtn.dataset.listId);
        const name = window.prompt('Rename this list:', list?.name || '');
        if (renameList(renameBtn.dataset.listId, name)) {
          renderListPickers();
          await saveWishlist();
          await renderWishlistPage();
        }
        return;
      }

      const deleteBtn = e.target.closest('.wishlist-list-header__delete');
      if (deleteBtn) {
        const list = findList(deleteBtn.dataset.listId);
        if (!list) return;
        if (list.items.length === 0) {
          deleteList(list.id);
          activeListId = PRIMARY_LIST_ID;
          renderListPickers();
          await saveWishlist();
          await renderWishlistPage();
        } else {
          pendingDeleteListId = list.id;
          await renderWishlistPage();
        }
        return;
      }

      const shareEnableBtn = e.target.closest('.wishlist-list-header__share-enable');
      if (shareEnableBtn) {
        setListSharing(shareEnableBtn.dataset.listId, true);
        await renderWishlistPage(); // shows "Turning on sharing..." immediately
        await saveWishlist(); // resolves to the real token via the server response
        await renderWishlistPage();
        return;
      }

      const shareDisableBtn = e.target.closest('.wishlist-share-panel__disable');
      if (shareDisableBtn) {
        setListSharing(shareDisableBtn.dataset.listId, false);
        await saveWishlist();
        await renderWishlistPage();
        return;
      }

      const shareCopyBtn = e.target.closest('.wishlist-share-panel__copy');
      if (shareCopyBtn) {
        try {
          await navigator.clipboard.writeText(shareCopyBtn.dataset.url);
          showToast('Link copied.');
        } catch {
          showToast("Couldn't copy the link -- select and copy it manually.");
        }
        return;
      }

      const cancelDelete = e.target.closest('.wishlist-delete-confirm__cancel');
      if (cancelDelete) {
        pendingDeleteListId = null;
        await renderWishlistPage();
        return;
      }

      const deleteAnyway = e.target.closest('.wishlist-delete-confirm__delete-anyway');
      if (deleteAnyway) {
        deleteList(pendingDeleteListId);
        activeListId = PRIMARY_LIST_ID;
        pendingDeleteListId = null;
        renderListPickers();
        await saveWishlist();
        await renderWishlistPage();
        return;
      }

      const moveDelete = e.target.closest('.wishlist-delete-confirm__move-delete');
      if (moveDelete) {
        const select = moveDelete.parentElement.querySelector('.wishlist-delete-confirm__target');
        if (!select.value) return;
        deleteList(pendingDeleteListId, { moveItemsTo: select.value });
        activeListId = select.value;
        pendingDeleteListId = null;
        renderListPickers();
        await saveWishlist();
        await renderWishlistPage();
        return;
      }

      const removeBtn = e.target.closest('.wishlist-card__remove');
      if (removeBtn) {
        removeItemFromList(removeBtn.dataset.productId, removeBtn.dataset.listId, removeBtn.dataset.variantId || null);
        renderHearts();
        renderListPickers();
        await saveWishlist();
        await renderWishlistPage();
        return;
      }

      const addToCartBtn = e.target.closest('.wishlist-card__add-to-cart');
      if (addToCartBtn) {
        if (addToCartBtn.disabled) return;
        const originalText = addToCartBtn.textContent;
        addToCartBtn.disabled = true;
        addToCartBtn.textContent = 'Adding…';

        // data-quantity is the item's saved quantity on a cart-kind list
        // (see renderListItems), 1 on a plain wishlist item.
        const quantity = parseInt(addToCartBtn.dataset.quantity, 10) || 1;
        const result = await addVariantsToCart([{ id: addToCartBtn.dataset.variantId, quantity }]);
        showToast(result.success ? 'Added to cart.' : (result.error || "Couldn't add to cart."));

        addToCartBtn.disabled = false;
        addToCartBtn.textContent = originalText;
        return;
      }

      const addAllBtn = e.target.closest('.wishlist-list-header__add-all');
      if (addAllBtn) {
        if (addAllBtn.disabled) return;

        // activeListAddableItems is set by the most recent renderListItems
        // call for whatever list is on screen -- items without a
        // purchasable variant were already excluded there, so the count
        // difference against the list's own item count is exactly how many
        // are being skipped as sold out. Each item's own quantity carries
        // through (1 for a plain wishlist item, the saved amount for a
        // cart-kind list) -- restoring a saved cart applies real quantities,
        // not just 1 of each (#30).
        const items = activeListAddableItems.map(item => ({ id: item.variantId, quantity: item.quantity }));
        const activeList = findList(activeListId);
        const isCart = activeList?.kind === 'cart';
        const skipped = (activeList?.items.length || 0) - items.length;

        if (items.length === 0) {
          showToast('Nothing available to add right now.');
          return;
        }

        const originalText = addAllBtn.textContent;
        addAllBtn.disabled = true;
        addAllBtn.textContent = isCart ? 'Restoring…' : 'Adding…';

        const result = await addVariantsToCart(items);
        if (result.success) {
          // Restoring a saved cart keeps it for reuse -- it's not cleared
          // or deleted here (decided 2026-09-01, #30): the main use case is
          // a repeat/recurring order, not a one-time snapshot.
          const addedLabel = isCart
            ? `Restored ${items.length} item${items.length === 1 ? '' : 's'} to your cart`
            : `Added ${items.length} item${items.length === 1 ? '' : 's'} to cart`;
          showToast(skipped > 0 ? `${addedLabel} -- ${skipped} sold out and ${skipped === 1 ? 'was' : 'were'} skipped.` : `${addedLabel}.`);
        } else {
          showToast(result.error || "Couldn't add to cart.");
        }

        addAllBtn.disabled = false;
        addAllBtn.textContent = originalText;
        return;
      }

      const saveCartBtn = e.target.closest('.wishlist-save-cart-button');
      if (saveCartBtn) {
        if (saveCartBtn.disabled) return;

        // window.prompt pre-filled with the same default saveCurrentCartAsList
        // falls back to itself -- so hitting OK without typing anything and
        // clicking Cancel both do something sensible: a real save either way,
        // or no save at all.
        const name = window.prompt('Name this saved cart:', `Cart saved ${formatShortDate(new Date())}`);
        if (name === null) return;

        const originalText = saveCartBtn.textContent;
        saveCartBtn.disabled = true;
        saveCartBtn.textContent = 'Saving…';

        const result = await saveCurrentCartAsList(name);

        saveCartBtn.disabled = false;
        saveCartBtn.textContent = originalText;

        if (result.success) {
          showToast(
            result.skipped
              ? `Cart saved -- ${result.skipped} subscription item${result.skipped === 1 ? '' : 's'} skipped.`
              : 'Cart saved. Find it under Saved Carts on your <a href="/pages/wishlist">wishlist page</a>.'
          );
        } else {
          showToast(result.error || "Couldn't save this cart.");
        }
        return;
      }
    });

    document.addEventListener('change', async e => {
      const target = e.target.closest('.wishlist-delete-confirm__target');
      if (target) {
        const moveDeleteBtn = target.parentElement.querySelector('.wishlist-delete-confirm__move-delete');
        moveDeleteBtn.disabled = !target.value;
        return;
      }

      const qtyInput = e.target.closest('.wishlist-card__quantity');
      if (qtyInput) {
        const clamped = Math.max(1, Math.min(999, parseInt(qtyInput.value, 10) || 1));
        qtyInput.value = clamped;
        const list = findList(qtyInput.dataset.listId);
        const item = list?.items.find(i => itemMatches(i, qtyInput.dataset.productId, qtyInput.dataset.variantId || null));
        if (item) {
          item.quantity = clamped;
          await saveWishlist();
          // No full renderWishlistPage() -- that would rebuild the input
          // mid-edit and drop the field's own focus. The add-to-cart
          // button's data-quantity is the only other place this number is
          // shown, so it's the only other thing that needs to catch up.
          const card = qtyInput.closest('.wishlist-card');
          const addBtn = card?.querySelector('.wishlist-card__add-to-cart');
          if (addBtn) {
            addBtn.dataset.quantity = clamped;
            if (!addBtn.disabled) addBtn.textContent = clamped > 1 ? `Add ${clamped} to Cart` : 'Add to Cart';
          }
        }
        return;
      }

      const moveTo = e.target.closest('.wishlist-card__move-to');
      if (moveTo && moveTo.value) {
        const moved = moveItem(moveTo.dataset.productId, moveTo.dataset.listId, moveTo.value, moveTo.dataset.variantId || null);
        if (moved) {
          showToast('Moved to your other list.');
          renderHearts();
          renderListPickers();
          await saveWishlist();
          await renderWishlistPage();
        }
        return;
      }

      const notifyToggle = e.target.closest('.wishlist-notify-toggle');
      if (notifyToggle) {
        const enabled = notifyToggle.checked;
        notifyToggle.disabled = true;
        try {
          const response = await fetch('/apps/wishlist/notify-preference', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ enabled }),
          });
          const result = await response.json();
          if (result.success) {
            notifyPreferenceEnabled = result.enabled;
            showToast(enabled ? 'Restock and price-drop alerts turned on.' : 'Restock and price-drop alerts turned off.');
          } else {
            notifyToggle.checked = !enabled;
            showToast("Couldn't update your preference. Please try again.");
          }
        } catch (err) {
          console.error('Backbone Wishlist: Failed to update notify preference', err);
          notifyToggle.checked = !enabled;
          showToast("Couldn't update your preference. Please try again.");
        }
        notifyToggle.disabled = false;
        return;
      }
    });
  }

  // ─── VARIANT TRACKING (product page only) ──────────────────────────────────
  // Reads whichever variant is currently selected via the theme's own
  // product form -- input[name="id"] is what product-form.js actually
  // submits to /cart/add, so it's the single source of truth for "what's
  // selected right now," already kept correct by the theme itself. Only
  // trusted when a real picker exists: a single-variant product has no
  // picker and nothing to choose, so it's left null and treated exactly
  // like pre-#9 data (product-level, no specific variant recorded).
  function getSelectedVariantId() {
    if (!document.querySelector('variant-selects, variant-radios')) return null;
    const input = document.querySelector('product-form form [name="id"]') || document.querySelector('form[action*="/cart/add"] [name="id"]');
    return input?.value ? String(input.value) : null;
  }

  // product-info.js publishes this (see assets/product-info.js) only once
  // a full variant selection has actually resolved -- not on every
  // incomplete option click -- with the resolved variant object already
  // attached, so no extra DOM read or Storefront round trip is needed here.
  function bindVariantChangeListener() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      currentProductVariantId = data?.variant?.id ? String(data.variant.id) : null;
      renderHearts();
      renderListPickers();
    });
  }

  // ─── GUEST MERGE ON LOGIN ──────────────────────────────────────────────────
  // Folds any lists saved while browsing as a guest into the customer's
  // account. Runs automatically on init() for logged-in customers (so it
  // fires on the first page load after login).
  //
  // Merge rule: the guest's primary list folds into the customer's primary
  // list. A guest's custom list merges into an existing customer list of
  // the same name (case-insensitive) if one exists, otherwise it's created
  // as a new list. Items are de-duped by product + variant.
  async function mergeGuestWishlist() {
    const guestLists = loadFromLocalStorage();
    const hasGuestItems = guestLists.some(list => list.items.length > 0);
    if (!hasGuestItems) return;

    guestLists.forEach(guestList => {
      let target;
      if (guestList.primary) {
        target = getPrimaryList();
      } else {
        target = lists.find(l => !l.primary && l.name.trim().toLowerCase() === guestList.name.trim().toLowerCase());
        if (!target) {
          // Guests can only ever build up a primary list (createList is
          // logged-in-only, see above), so guestList.kind is always
          // "wishlist" in practice -- carried through anyway rather than
          // hardcoded, since this is the one spot a guest list's own kind
          // ever flows into a brand new customer list.
          target = { id: generateListId(), name: guestList.name, primary: false, kind: guestList.kind || 'wishlist', items: [] };
          lists.push(target);
        }
      }
      guestList.items.forEach(item => {
        if (!target.items.some(existing => existing.productId === item.productId && existing.variantId === item.variantId)) {
          target.items.push(item);
        }
      });
    });

    await saveToServer();
    localStorage.removeItem(STORAGE_KEY);
  }

  return { init, isInWishlist, mergeGuestWishlist, renderWishlistPage };

})();

document.addEventListener('DOMContentLoaded', () => BackboneWishlist.init());
